/**
 * 音频播放器 Hook
 * v6.0 新增：支持 TTS 音频流播放
 * 
 * 支持的音频格式：
 * - MP3/WAV/OGG：使用 decodeAudioData（容器格式）
 * - PCM：手动创建 AudioBuffer（16-bit signed integer, little-endian）
 * - Opus：使用 opus-decoder WASM 库
 * 
 * 核心技术：Web Audio API Scheduled Playback
 * - 使用 AudioContext.currentTime 进行精确时间调度
 * - 音频片段在精确时间点无缝衔接
 * - 解决片段间隙导致的"断断续续"问题
 * 
 * 自适应 Jitter Buffer 策略：
 * 1. 测量包到达间隔的抖动（Jitter）
 * 2. 根据抖动动态计算初始缓冲时间
 * 3. 首次播放前等待最少包数，之后实时处理
 * 4. tts_end 信号：立即播放所有剩余音频
 * 
 * 性能优化：
 * - 队列操作不触发 React 渲染
 * - 只在关键状态变化时更新 state（使用 ref 去重）
 * - 并行解码 + 顺序调度
 * - 音频解码失败时跳过继续播放
 * 
 * 抖动计算参考 RFC 3550（RTP 协议）
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { OpusDecoder } from 'opus-decoder'

// 自适应缓冲配置
const MIN_INITIAL_BUFFER_MS = 150     // 最小初始缓冲时间
const MAX_INITIAL_BUFFER_MS = 600     // 最大初始缓冲时间
const DEFAULT_INITIAL_BUFFER_MS = 300 // 默认初始缓冲时间（第一次 TTS 使用）
const JITTER_ALPHA = 0.125            // 抖动计算平滑系数（RFC 3550 推荐值）
const MIN_QUEUE_FOR_FIRST_PLAY = 2    // 首次播放前最少需要的音频包数
const SCHEDULE_AHEAD_TIME = 0.05      // 提前调度时间（秒），避免调度延迟

// 支持的音频格式
export type AudioFormat = 'mp3' | 'wav' | 'ogg' | 'pcm' | 'opus'

// 音频元数据（从 tts_start 消息获取）
export interface AudioMetadata {
  format: AudioFormat
  sampleRate: number    // 采样率 (Hz)
  channels?: number     // 通道数，默认 1
  bitDepth?: number     // 位深度，仅 PCM 格式，默认 16
}

interface AudioPlayerState {
  isPlaying: boolean
  isPaused: boolean
  error: string | null
  isBuffering: boolean
}

interface AudioPlayerOptions {
  onPlayStart?: () => void
  onPlayEnd?: () => void
  onError?: (error: string) => void
  onBuffering?: (isBuffering: boolean) => void
}

// 队列中的音频数据项
interface QueuedAudio {
  base64Data: string
  metadata: AudioMetadata
}

/**
 * 音频播放器 Hook
 * 支持 Base64 音频数据的流式播放，带自适应 Jitter Buffer 和无缝播放
 */
export function useAudioPlayer(options: AudioPlayerOptions = {}) {
  const { onPlayStart, onPlayEnd, onError: _onError, onBuffering } = options

  // 只保留必要的 UI 状态
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    isPaused: false,
    error: null,
    isBuffering: false,
  })

  // 音频队列（不触发渲染）
  const audioQueueRef = useRef<QueuedAudio[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())
  
  // 当前 TTS 会话的元数据（从 tts_start 消息设置）
  const currentMetadataRef = useRef<AudioMetadata | null>(null)
  
  // Scheduled Playback：下一个音频片段应该开始的时间
  const nextStartTimeRef = useRef<number>(0)
  const isSchedulingRef = useRef(false)
  
  // 缓冲控制
  const initialBufferTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasStartedPlayingRef = useRef(false)
  const isStreamEndedRef = useRef(false)
  const scheduledCountRef = useRef(0) // 已调度但未播放完的音频数
  
  // 状态去重 ref（避免频繁 setState）
  const isPlayingRef = useRef(false)
  const isBufferingRef = useRef(false)
  
  // 自适应缓冲：抖动测量
  const lastArrivalTimeRef = useRef<number>(0)
  const avgPacketIntervalRef = useRef<number>(100)
  const estimatedJitterRef = useRef<number>(0)
  const packetCountRef = useRef<number>(0)
  
  // 跨 TTS 会话保持的抖动历史
  const historicalJitterRef = useRef<number>(0)
  
  // Opus 解码器（懒加载，仅在需要时初始化）
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const opusDecoderRef = useRef<OpusDecoder<any> | null>(null)
  const opusDecoderReadyRef = useRef<Promise<void> | null>(null)

  // 获取或创建 AudioContext
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    }
    return audioContextRef.current
  }, [])

  // Base64 转 ArrayBuffer
  const base64ToArrayBuffer = useCallback((base64: string): ArrayBuffer => {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }, [])

  // 设置播放状态（带去重）
  const setPlaying = useCallback((playing: boolean) => {
    if (isPlayingRef.current !== playing) {
      isPlayingRef.current = playing
      setState(prev => ({ ...prev, isPlaying: playing }))
    }
  }, [])

  // 设置缓冲状态（带去重）
  const setBuffering = useCallback((buffering: boolean) => {
    if (isBufferingRef.current !== buffering) {
      isBufferingRef.current = buffering
      setState(prev => ({ ...prev, isBuffering: buffering }))
      onBuffering?.(buffering)
    }
  }, [onBuffering])

  // 计算建议的初始缓冲时间
  const calculateBufferTime = useCallback(() => {
    const jitter = historicalJitterRef.current > 0 
      ? historicalJitterRef.current 
      : estimatedJitterRef.current
    
    if (jitter <= 0) {
      return DEFAULT_INITIAL_BUFFER_MS
    }
    
    // 缓冲时间 = 2.5 × 抖动 + 100ms 安全边际
    const suggestedBuffer = jitter * 2.5 + 100
    const clampedBuffer = Math.min(MAX_INITIAL_BUFFER_MS, Math.max(MIN_INITIAL_BUFFER_MS, suggestedBuffer))
    
    console.log('[AudioPlayer] 计算缓冲时间:', {
      jitter: Math.round(jitter),
      suggested: Math.round(suggestedBuffer),
      clamped: Math.round(clampedBuffer),
    })
    
    return clampedBuffer
  }, [])

  // 更新抖动估计（不触发渲染）
  const updateJitterEstimate = useCallback(() => {
    const now = Date.now()
    packetCountRef.current++
    
    if (lastArrivalTimeRef.current > 0) {
      const interval = now - lastArrivalTimeRef.current
      const deviation = Math.abs(interval - avgPacketIntervalRef.current)
      
      estimatedJitterRef.current = estimatedJitterRef.current + 
        (deviation - estimatedJitterRef.current) * JITTER_ALPHA
      
      avgPacketIntervalRef.current = avgPacketIntervalRef.current + 
        (interval - avgPacketIntervalRef.current) * JITTER_ALPHA
      
      // 降低日志频率
      if (packetCountRef.current % 20 === 0) {
        console.log('[AudioPlayer] 抖动统计:', {
          packets: packetCountRef.current,
          avgInterval: Math.round(avgPacketIntervalRef.current),
          jitter: Math.round(estimatedJitterRef.current),
          queue: audioQueueRef.current.length,
          scheduled: scheduledCountRef.current,
        })
      }
    }
    
    lastArrivalTimeRef.current = now
  }, [])

  // 重置会话级抖动统计（保留历史抖动）
  const resetSessionStats = useCallback(() => {
    lastArrivalTimeRef.current = 0
    avgPacketIntervalRef.current = 100
    estimatedJitterRef.current = 0
    packetCountRef.current = 0
    currentMetadataRef.current = null
    console.log('[AudioPlayer] 重置会话统计')
  }, [])

  // 检查播放是否完成
  const checkPlaybackComplete = useCallback(() => {
    if (isStreamEndedRef.current && 
        audioQueueRef.current.length === 0 && 
        scheduledCountRef.current === 0) {
      console.log('[AudioPlayer] 播放完成')
      
      // 保存抖动历史
      if (estimatedJitterRef.current > 0) {
        historicalJitterRef.current = estimatedJitterRef.current
      }
      
      setPlaying(false)
      setBuffering(false)
      hasStartedPlayingRef.current = false
      isStreamEndedRef.current = false
      nextStartTimeRef.current = 0
      onPlayEnd?.()
    }
  }, [onPlayEnd, setPlaying, setBuffering])

  // ============ 多格式解码器 ============

  /**
   * 解码容器格式（MP3, WAV, OGG）
   * 使用浏览器原生 decodeAudioData
   */
  const decodeContainerFormat = useCallback(async (
    arrayBuffer: ArrayBuffer
  ): Promise<AudioBuffer | null> => {
    const audioContext = getAudioContext()
    try {
      return await audioContext.decodeAudioData(arrayBuffer)
    } catch (err) {
      console.warn('[AudioPlayer] 容器格式解码失败:', err)
      return null
    }
  }, [getAudioContext])

  /**
   * 解码 PCM 格式
   * 16-bit signed integer, little-endian
   */
  const decodePCM = useCallback((
    arrayBuffer: ArrayBuffer,
    sampleRate: number,
    channels: number = 1,
    bitDepth: number = 16
  ): AudioBuffer | null => {
    const audioContext = getAudioContext()
    
    try {
      const bytesPerSample = bitDepth / 8
      const numSamples = arrayBuffer.byteLength / bytesPerSample / channels
      
      if (numSamples <= 0) {
        console.warn('[AudioPlayer] PCM 数据为空')
        return null
      }
      
      // 创建 AudioBuffer
      const audioBuffer = audioContext.createBuffer(channels, numSamples, sampleRate)
      
      // 使用 DataView 读取 little-endian 数据
      const dataView = new DataView(arrayBuffer)
      
      for (let channel = 0; channel < channels; channel++) {
        const channelData = audioBuffer.getChannelData(channel)
        
        for (let i = 0; i < numSamples; i++) {
          // 交错存储：sample[i] 的 channel[c] 在位置 (i * channels + c) * bytesPerSample
          const byteOffset = (i * channels + channel) * bytesPerSample
          
          if (bitDepth === 16) {
            // 16-bit signed integer, little-endian -> Float32 [-1, 1]
            const sample = dataView.getInt16(byteOffset, true) // true = little-endian
            channelData[i] = sample / 32768
          } else if (bitDepth === 8) {
            // 8-bit unsigned integer -> Float32 [-1, 1]
            const sample = dataView.getUint8(byteOffset)
            channelData[i] = (sample - 128) / 128
          } else {
            console.warn('[AudioPlayer] 不支持的位深度:', bitDepth)
            return null
          }
        }
      }
      
      return audioBuffer
    } catch (err) {
      console.warn('[AudioPlayer] PCM 解码失败:', err)
      return null
    }
  }, [getAudioContext])

  /**
   * 获取或创建 Opus 解码器（懒加载）
   * Opus 支持的采样率：8000, 12000, 16000, 24000, 48000
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getOpusDecoder = useCallback(async (channels: number): Promise<OpusDecoder<any>> => {
    // 如果解码器已存在且参数匹配，直接返回
    if (opusDecoderRef.current) {
      return opusDecoderRef.current
    }
    
    // 如果正在初始化，等待完成
    if (opusDecoderReadyRef.current) {
      await opusDecoderReadyRef.current
      if (opusDecoderRef.current) {
        return opusDecoderRef.current
      }
    }
    
    // 创建新的解码器（不指定 sampleRate，使用默认 48000）
    // 解码后的 sampleRate 由 Opus 帧自动确定
    console.log('[AudioPlayer] 初始化 Opus 解码器:', { channels })
    const decoder = new OpusDecoder({
      channels,
    })
    
    // 保存 ready Promise，避免重复初始化
    opusDecoderReadyRef.current = decoder.ready.then(() => {
      opusDecoderRef.current = decoder
      console.log('[AudioPlayer] Opus 解码器已就绪')
    })
    
    await opusDecoderReadyRef.current
    return decoder
  }, [])

  /**
   * 解析 Opus 帧（防御性兼容代码）
   * 
   * 【后端确认格式 - 2025-01-12】
   * 后端使用 **格式 C - 单帧裸数据**：
   * - 每个 audio 消息 = 一个裸 Opus 帧（20ms 音频）
   * - 无长度前缀，直接是 Opus 帧数据
   * - 每秒约 50 条 audio 消息
   * 
   * 【保留此函数的原因】
   * 作为防御性代码，兼容可能的格式变化：
   * - 格式 A：带长度前缀 [2字节长度(big-endian)][Opus帧]...
   * - 格式 B：裸 Opus 帧
   * - 格式 C：单帧裸数据（当前使用）
   * 
   * 正常情况下此函数直接返回 [data]（单帧），不会真正解析。
   */
  const parseOpusFrames = useCallback((data: Uint8Array): Uint8Array[] => {
    const frames: Uint8Array[] = []
    let offset = 0
    
    while (offset + 2 <= data.length) {
      // 读取 2 字节长度前缀（big-endian）
      const frameLength = (data[offset] << 8) | data[offset + 1]
      offset += 2
      
      if (frameLength === 0 || offset + frameLength > data.length) {
        // 无效长度或数据不足，说明是裸帧（格式 C）
        if (frames.length === 0) {
          // 正常情况：后端发送的是单帧裸数据
          return [data]
        }
        break
      }
      
      // 提取帧数据（格式 A：带长度前缀）
      frames.push(data.slice(offset, offset + frameLength))
      offset += frameLength
    }
    
    // 如果没有解析出帧，当作裸帧处理
    if (frames.length === 0) {
      return [data]
    }
    
    return frames
  }, [])

  /**
   * 解码 Opus 格式
   * 使用 opus-decoder WASM 库
   * 
   * 【后端确认格式 - 2025-01-12】
   * - 每个 audio 消息 = 一个裸 Opus 帧（20ms）
   * - 每秒约 50 条消息，因此减少日志输出
   * 
   * API 说明：
   * - decodeFrame(opusFrame): 解码单个 Opus 帧
   * - decodeFrames(opusFrames): 解码帧数组 (Array of Uint8Arrays)
   */
  const decodeOpus = useCallback(async (
    arrayBuffer: ArrayBuffer,
    _sampleRate: number,  // Opus 解码器自动确定采样率
    channels: number = 1
  ): Promise<AudioBuffer | null> => {
    const audioContext = getAudioContext()
    
    try {
      // 获取或初始化 Opus 解码器
      const decoder = await getOpusDecoder(channels)
      
      // 解析 Opus 帧（正常情况直接返回单帧，防御性兼容多帧格式）
      const rawData = new Uint8Array(arrayBuffer)
      const opusFrames = parseOpusFrames(rawData)
      
      // 解码帧（正常情况是单帧）
      let decoded
      if (opusFrames.length === 1) {
        decoded = decoder.decodeFrame(opusFrames[0])
      } else {
        // 防御性处理：如果解析出多帧，使用 decodeFrames
        console.log('[AudioPlayer] Opus 多帧数据:', opusFrames.length, '帧')
        decoded = decoder.decodeFrames(opusFrames)
      }
      
      if (!decoded || decoded.samplesDecoded === 0) {
        console.warn('[AudioPlayer] Opus 解码返回空数据, 输入长度:', rawData.length)
        return null
      }
      
      // 检查解码错误
      if (decoded.errors && decoded.errors.length > 0) {
        console.warn('[AudioPlayer] Opus 解码有错误:', decoded.errors)
        // 继续处理，可能只是部分帧有问题
      }
      
      // 创建 AudioBuffer
      const numChannels = decoded.channelData.length
      const numSamples = decoded.samplesDecoded
      const audioBuffer = audioContext.createBuffer(numChannels, numSamples, decoded.sampleRate)
      
      // 填充每个通道的数据（使用 Float32Array 复制）
      for (let ch = 0; ch < numChannels; ch++) {
        const channelData = audioBuffer.getChannelData(ch)
        channelData.set(decoded.channelData[ch])
      }
      
      return audioBuffer
    } catch (err) {
      console.warn('[AudioPlayer] Opus 解码失败:', err)
      return null
    }
  }, [getAudioContext, getOpusDecoder, parseOpusFrames])

  /**
   * 根据格式选择解码器
   */
  const decodeAudio = useCallback(async (
    base64Data: string,
    metadata: AudioMetadata
  ): Promise<AudioBuffer | null> => {
    const arrayBuffer = base64ToArrayBuffer(base64Data)
    const { format, sampleRate, channels = 1, bitDepth = 16 } = metadata
    
    switch (format) {
      case 'pcm':
        return decodePCM(arrayBuffer, sampleRate, channels, bitDepth)
      
      case 'opus':
        return decodeOpus(arrayBuffer, sampleRate, channels)
      
      case 'mp3':
      case 'wav':
      case 'ogg':
      default:
        return decodeContainerFormat(arrayBuffer)
    }
  }, [base64ToArrayBuffer, decodePCM, decodeOpus, decodeContainerFormat])

  // ============ 播放调度 ============

  // 调度单个 AudioBuffer（同步，不含解码）
  const scheduleBuffer = useCallback((audioBuffer: AudioBuffer) => {
    const audioContext = getAudioContext()
    
    const source = audioContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(audioContext.destination)

    // 计算精确的开始时间
    const currentTime = audioContext.currentTime
    
    // 如果 nextStartTime 已经过去或未设置，从当前时间 + 少量延迟开始
    if (nextStartTimeRef.current <= currentTime) {
      nextStartTimeRef.current = currentTime + SCHEDULE_AHEAD_TIME
    }

    const startTime = nextStartTimeRef.current
    
    // 在精确时间点开始播放
    source.start(startTime)
    
    // 更新下一个片段的开始时间（紧接在这个片段结束后）
    nextStartTimeRef.current = startTime + audioBuffer.duration
    
    // 跟踪活跃的音频源
    activeSourcesRef.current.add(source)
    scheduledCountRef.current++
    
    source.onended = () => {
      activeSourcesRef.current.delete(source)
      scheduledCountRef.current--
      checkPlaybackComplete()
    }
  }, [getAudioContext, checkPlaybackComplete])

  // 处理音频队列：并行解码 + 顺序调度
  const processQueue = useCallback(async () => {
    if (isSchedulingRef.current) return
    
    // 队列为空
    if (audioQueueRef.current.length === 0) {
      if (isStreamEndedRef.current) {
        checkPlaybackComplete()
      }
      // 稳态时不触发 setBuffering，避免频繁渲染
      return
    }
    
    // 首次播放：等待足够的包
    if (!hasStartedPlayingRef.current) {
      if (!isStreamEndedRef.current && audioQueueRef.current.length < MIN_QUEUE_FOR_FIRST_PLAY) {
        // 等待更多包，不触发渲染
        return
      }
      hasStartedPlayingRef.current = true
      console.log('[AudioPlayer] 首次播放条件满足，队列:', audioQueueRef.current.length)
    }
    
    // 开始处理
    isSchedulingRef.current = true
    setPlaying(true)
    setBuffering(false)
    
    // 取出所有待处理的包
    const chunksToProcess = audioQueueRef.current.splice(0)
    
    // 确保 AudioContext 处于运行状态
    const audioContext = getAudioContext()
    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }
    
    // 并行解码
    const decodePromises = chunksToProcess.map(async (item) => {
      return decodeAudio(item.base64Data, item.metadata)
    })
    
    const audioBuffers = await Promise.all(decodePromises)
    
    // 顺序调度（过滤掉解码失败的 null）
    for (const buffer of audioBuffers) {
      if (buffer) {
        scheduleBuffer(buffer)
      }
    }
    
    isSchedulingRef.current = false
    
    // 检查是否有新包在处理期间到达，或者流已结束
    if (audioQueueRef.current.length > 0 || isStreamEndedRef.current) {
      queueMicrotask(() => processQueue())
    }
  }, [getAudioContext, decodeAudio, scheduleBuffer, checkPlaybackComplete, setPlaying, setBuffering])

  // ============ 公开 API ============

  /**
   * 设置当前 TTS 会话的元数据
   * 在收到 tts_start 消息时调用
   */
  const setMetadata = useCallback((metadata: AudioMetadata) => {
    currentMetadataRef.current = metadata
    console.log('[AudioPlayer] 设置元数据:', metadata)
  }, [])

  /**
   * 添加音频到队列
   * @param base64Audio Base64 编码的音频数据
   * @param format 可选的格式覆盖（默认使用 setMetadata 设置的格式）
   */
  const queueAudio = useCallback((base64Audio: string, format?: AudioFormat) => {
    updateJitterEstimate()
    
    // 使用传入的格式或当前元数据中的格式
    const metadata: AudioMetadata = currentMetadataRef.current 
      ? { ...currentMetadataRef.current, ...(format ? { format } : {}) }
      : { format: format || 'mp3', sampleRate: 22050, channels: 1 }
    
    audioQueueRef.current.push({ base64Data: base64Audio, metadata })
    
    // 第一包到达：启动预缓冲定时器
    if (audioQueueRef.current.length === 1 && !hasStartedPlayingRef.current && !initialBufferTimerRef.current) {
      const bufferMs = calculateBufferTime()
      console.log('[AudioPlayer] 第一包到达，预缓冲', Math.round(bufferMs), 'ms, 格式:', metadata.format)
      
      onPlayStart?.()
      setBuffering(true)
      
      initialBufferTimerRef.current = setTimeout(() => {
        console.log('[AudioPlayer] 预缓冲结束，开始播放，队列:', audioQueueRef.current.length)
        initialBufferTimerRef.current = null
        processQueue()
      }, bufferMs)
    }
    
    // 如果已经开始播放，且不在调度中，处理新到达的包
    if (hasStartedPlayingRef.current && !isSchedulingRef.current) {
      processQueue()
    }
  }, [processQueue, onPlayStart, setBuffering, updateJitterEstimate, calculateBufferTime])

  // 流结束信号
  const flush = useCallback(() => {
    console.log('[AudioPlayer] flush() - 流结束，队列:', audioQueueRef.current.length, '已调度:', scheduledCountRef.current)
    isStreamEndedRef.current = true
    
    // 取消预缓冲定时器，立即播放
    if (initialBufferTimerRef.current) {
      clearTimeout(initialBufferTimerRef.current)
      initialBufferTimerRef.current = null
      console.log('[AudioPlayer] 取消预缓冲，立即播放')
    }
    
    processQueue()
  }, [processQueue])

  // 停止播放
  const stop = useCallback(() => {
    console.log('[AudioPlayer] stop()')
    
    // 取消预缓冲定时器
    if (initialBufferTimerRef.current) {
      clearTimeout(initialBufferTimerRef.current)
      initialBufferTimerRef.current = null
    }
    
    // 清空队列
    audioQueueRef.current = []
    
    // 停止所有活跃的音频源
    activeSourcesRef.current.forEach(source => {
      try {
        source.stop()
      } catch {
        // 忽略
      }
    })
    activeSourcesRef.current.clear()

    // 重置所有状态
    isSchedulingRef.current = false
    hasStartedPlayingRef.current = false
    isStreamEndedRef.current = false
    scheduledCountRef.current = 0
    nextStartTimeRef.current = 0
    
    // 重置 ref 和 state
    isPlayingRef.current = false
    isBufferingRef.current = false
    
    setState({ 
      isPlaying: false, 
      isPaused: false, 
      isBuffering: false,
      error: null,
    })
    
    // 重置会话统计
    resetSessionStats()
  }, [resetSessionStats])

  // 暂停
  const pause = useCallback(() => {
    const audioContext = audioContextRef.current
    if (audioContext && audioContext.state === 'running') {
      audioContext.suspend()
      setState(prev => ({ ...prev, isPaused: true }))
    }
  }, [])

  // 恢复
  const resume = useCallback(() => {
    const audioContext = audioContextRef.current
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume()
      setState(prev => ({ ...prev, isPaused: false }))
    }
  }, [])

  // 清理
  useEffect(() => {
    return () => {
      stop()
      // 关闭 AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      // 释放 Opus 解码器
      if (opusDecoderRef.current) {
        opusDecoderRef.current.free()
        opusDecoderRef.current = null
        opusDecoderReadyRef.current = null
      }
    }
  }, [stop])

  return {
    ...state,
    isPlayingRef,  // 暴露 ref，供外部在回调中读取实时播放状态（避免闭包问题）
    setMetadata,
    queueAudio,
    flush,
    stop,
    pause,
    resume,
    resetSessionStats,
  }
}
